import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  Users,
  Clock,
  Activity,
  Calendar,
  TrendingUp,
  Eye,
  MousePointer,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  FileText,
  X,
  Zap,
  Target,
  LogIn,
  Timer,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { useActivityTracking } from '../../contexts/ActivityTrackingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';

const ActivityReports = () => {
  const { currentUser } = useAuth();
  const { isAdminOrHigher } = useRBAC();
  const activityTracking = useActivityTracking();
  
  // Handle case where context is not available
  const {
    fetchMonthlyStats,
    fetchActivitySummary,
    fetchTrackedUsers,
    getDetailedUserActivity,
    exportToCSV,
    monthlyStats,
    activitySummary,
    trackedUsers,
    allUsersActivities,
    fetchUserActivities,
    loading,
    canViewTracking
  } = activityTracking || {};

  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Admin or Developer can access
  const canAccess = isAdminOrHigher() && canViewTracking?.();

  useEffect(() => {
    if (canAccess) {
      fetchMonthlyStats?.();
      fetchUserActivities?.();
      fetchActivitySummary?.();
      fetchTrackedUsers?.();
    }
  }, [canAccess, fetchMonthlyStats, fetchUserActivities, fetchActivitySummary, fetchTrackedUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchMonthlyStats?.();
    await fetchUserActivities?.();
    await fetchActivitySummary?.();
    await fetchTrackedUsers?.();
    setRefreshing(false);
  };

  const handleViewUser = async (userEmail) => {
    setSelectedUser(userEmail);
    const details = await getDetailedUserActivity?.(userEmail);
    setUserDetails(details);
    setShowUserModal(true);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  // Chart data for daily activity
  const dailyChartData = useMemo(() => {
    if (!monthlyStats?.dailyActivity) return [];
    return Object.entries(monthlyStats.dailyActivity)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14) // Last 14 days
      .map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count
      }));
  }, [monthlyStats]);

  const maxDailyCount = Math.max(...dailyChartData.map(d => d.count), 1);

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Access Restricted</p>
          <p className="text-gray-400 text-sm mt-1">Only Admin and Developer roles can view activity tracking</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Monthly Activity Report</h2>
            <p className="text-sm text-gray-500">
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => exportToCSV(allUsersActivities, `activity_report_${new Date().toISOString().split('T')[0]}.csv`)}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Export CSV</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">Refresh</span>
          </motion.button>
        </div>
      </div>

      {loading && !monthlyStats ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Active Users</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats?.totalActiveUsers || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm text-green-600">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span>This month</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Logins</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats?.totalLogins || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <LogIn className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm text-blue-600">
                <Activity className="w-4 h-4 mr-1" />
                <span>Sessions started</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Page Visits</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats?.totalPageVisits || 0}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm text-purple-600">
                <MousePointer className="w-4 h-4 mr-1" />
                <span>Navigation events</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Activities</p>
                  <p className="text-3xl font-bold text-gray-900">{monthlyStats?.totalActivities || 0}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm text-orange-600">
                <Target className="w-4 h-4 mr-1" />
                <span>All tracked events</span>
              </div>
            </motion.div>
          </div>

          {/* Daily Activity Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600" />
              Daily Activity (Last 14 Days)
            </h3>
            <div className="h-48 flex items-end justify-between space-x-2">
              {dailyChartData.map((day, index) => (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${(day.count / maxDailyCount) * 100}%` }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg min-h-[4px] relative group cursor-pointer"
                  >
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {day.count} events
                    </div>
                  </motion.div>
                  <span className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">{day.date}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Page Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Pages Visited
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {monthlyStats?.pageStats && Object.entries(monthlyStats.pageStats)
                  .sort((a, b) => b[1].visits - a[1].visits)
                  .map(([page, stats], index) => (
                    <div key={page} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-700 capitalize">{page}</span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-500">{stats.visits} visits</span>
                        <span className="text-blue-600 font-medium flex items-center">
                          <Timer className="w-3 h-3 mr-1" />
                          {formatDuration(Math.round(stats.totalDuration / (stats.visits || 1)))} avg
                        </span>
                      </div>
                    </div>
                  ))}
                {(!monthlyStats?.pageStats || Object.keys(monthlyStats.pageStats).length === 0) && (
                  <p className="text-gray-500 text-center py-4">No page visits recorded</p>
                )}
              </div>
            </div>

            {/* Feature Usage */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-blue-600" />
                Feature Usage
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {monthlyStats?.featureStats && Object.entries(monthlyStats.featureStats)
                  .sort((a, b) => b[1] - a[1])
                  .map(([feature, count], index) => (
                    <div key={feature} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <span className="font-medium text-gray-700">{feature}</span>
                      </div>
                      <span className="text-purple-600 font-medium">{count} uses</span>
                    </div>
                  ))}
                {(!monthlyStats?.featureStats || Object.keys(monthlyStats.featureStats).length === 0) && (
                  <p className="text-gray-500 text-center py-4">No feature usage recorded</p>
                )}
              </div>
            </div>
          </div>

          {/* User Activity Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-600" />
                User Activity Overview
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Activity Count</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rank</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {monthlyStats?.mostActiveUsers?.map(([email, count], index) => (
                    <tr key={email} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-3">
                            <span className="text-white text-sm font-semibold">
                              {email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(count / (monthlyStats.mostActiveUsers[0]?.[1] || 1)) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-700">{count}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          index === 0 ? 'bg-yellow-100 text-yellow-800' :
                          index === 1 ? 'bg-gray-100 text-gray-800' :
                          index === 2 ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-50 text-blue-700'
                        }`}>
                          #{index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleViewUser(email)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-end space-x-1"
                        >
                          <span>View Details</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!monthlyStats?.mostActiveUsers || monthlyStats.mostActiveUsers.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  No user activity recorded this month
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* User Detail Modal */}
      <AnimatePresence>
        {showUserModal && userDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowUserModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <span className="text-white text-lg font-bold">
                      {selectedUser?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{selectedUser}</h3>
                    <p className="text-sm text-gray-500">User Activity Details</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">{userDetails.totalLogins}</p>
                    <p className="text-xs text-gray-600">Total Logins</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{userDetails.totalPageVisits}</p>
                    <p className="text-xs text-gray-600">Page Visits</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{formatDuration(userDetails.totalTimeSpentSeconds)}</p>
                    <p className="text-xs text-gray-600">Total Time</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{userDetails.totalActivities}</p>
                    <p className="text-xs text-gray-600">Activities</p>
                  </div>
                </div>

                {/* Login Info */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">First Activity</p>
                      <p className="font-medium text-gray-900">{formatDate(userDetails.firstActivity)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Last Login</p>
                      <p className="font-medium text-gray-900">{formatDate(userDetails.lastLogin)}</p>
                    </div>
                  </div>
                </div>

                {/* Time Per Page */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Time Spent Per Page
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(userDetails.timePerPage || {}).map(([page, seconds]) => (
                      <div key={page} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700 capitalize">{page}</span>
                        <span className="text-sm font-medium text-blue-600">{formatDuration(seconds)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activities */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Activity className="w-4 h-4 mr-2" />
                    Recent Activities
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userDetails.recentActivities?.slice(0, 20).map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          activity.event_type === 'login' ? 'bg-green-500' :
                          activity.event_type === 'page_visit' ? 'bg-blue-500' :
                          activity.event_type === 'button_click' ? 'bg-purple-500' :
                          'bg-gray-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 truncate">{activity.event_description}</p>
                          <p className="text-xs text-gray-500">{formatDate(activity.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Button */}
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => exportToCSV(userDetails.recentActivities, `${selectedUser}_activity.csv`)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span className="font-medium">Export User Activity</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityReports;
